import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { CrudPagedListComponent } from "../_directives";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import {
  AlertService,
  ProductCategoryService,
  StorageService,
} from "../_services";
import { Observable } from "rxjs";
import { Pager, ReportSessionInput } from "../_models";
import { ReportsService } from "../_services/reports.service";
import { takeUntil } from "rxjs/operators";
import { NgxSmartModalService } from "ngx-smart-modal";
import { isEmpty } from "lodash";
import { isRateOrPctField, reportsFieldDataV2, reportsFieldDataV3 } from "./utils/reportsFieldData";
import { CustomerJourneyType, ReportDimensions, ReportFieldData } from "./models/reports";
import * as moment from 'moment-timezone';

@Component({
  moduleId: module.id.toString(),
  selector: "reports-table-v2",
  templateUrl: "./templates/report-table-v2.component.html",
  styleUrls: ["./styles/report-table-v2.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ReportTableV2Component
  extends CrudPagedListComponent
  implements OnInit, OnChanges, OnDestroy
{
  report$: Observable<ReportSessionInput[]> = this.data$;
  @Input("reportReq") reportReq: any;
  @Input("fields") fields: string[] = [];
  @Input("isCustomReportLoading") isCustomReportLoading: boolean;
  @Input("inputReportService") inputReportService: ReportsService;
  @Input("getFieldValue") getFieldValue;
  @Input("isChatbotReport") isChatbotReport: boolean;
  @Input('isGatewayTransactionReport') isGatewayTransactionReport: boolean;
  @Input('showSummaryRow') showSummaryRow: boolean = false;
  @Input("ordering") ordering: string[] = [];
  @Input('isV3Report') isV3Report: boolean = false;
  @Input('fieldLabels') fieldLabels: { [key: string]: string } = {};
  // Set by retention reports so Journey ID cells open the Customer Journey Report.
  @Input('journeyType') journeyType: CustomerJourneyType = null;

  @Output() isReportGenerated: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output('onOrder') onOrder: EventEmitter<string[]> = new EventEmitter<string[]>()
  selectedFields: string[] = [];
  isTableLoading: boolean = true;
  public hasTotalsRow: boolean = false;
  public summaryData: object = null;
  public selectedSessionId: number | string = null;
  public isQuestionExpanded: boolean[] = [];
  public isAnswerExpanded: boolean[] = [];
  public totalResultCount: number = 0;

  rowsPerPageOptions = [
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: 250, label: "250" },
    { value: 500, label: "500" },
    { value: 1000, label: "1000" },
  ];

  private defaultFields = [
    "date_joined",
    "campaign.name",
    "result",
    "source",
    "customers.first_name",
    "customers.last_name",
    "device_info.device_type",
    "is_billable",
  ];
  pageSize = 50;
  noTableData = false;
  isSavedReport = false;
  hasPctData = false;

  protected fieldData: ReportFieldData = reportsFieldDataV2;

  public campaignCategoriesMap = {};
  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected reportsService: ReportsService,
    protected alertService: AlertService,
    protected categoryService: ProductCategoryService,
    protected modalService: NgxSmartModalService,
    protected storageService: StorageService
  ) {
    super(router, location, route, reportsService, alertService);
    this.objectName = "report-table";

    if (!this.fields || !this.fields.length) {
      this.fields = this.defaultFields;
    } else {
      if (this.fields.includes("exclude_zeroes")) {
        this.fields = this.fields.filter((field) => field !== "exclude_zeroes");
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.fields.includes("exclude_zeroes")) {
      this.fields = this.fields.filter((field) => field !== "exclude_zeroes");
    }

    if (changes.isV3Report) {
      this.fieldData = this.isV3Report ? reportsFieldDataV3 : reportsFieldDataV2;
    }

    if (changes.isCustomReportLoading) {
      if (!this.isCustomReportLoading && this.isSavedReport) {
        this.queryData();
      }
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.ordering = [];
    if (this.inputReportService) {
      this.reportsService = this.inputReportService;
    }
    this.route.params.subscribe((params) => {
      if (!params.id) {
        this.queryData();
      } else {
        this.isSavedReport = true;
      }
    });
  }

  ngOnDestroy() {
    this.ordering = [];
    super.ngOnDestroy();
  }

  public queryData(selectedList?: string[]) {
    this.isTableLoading = true;
    this.totalResultCount = 0;
    this.noTableData = false;
    this.selectedFields = selectedList || this.fields;
    if (this.selectedFields.includes("exclude_zeroes")) {
      this.selectedFields = this.selectedFields.filter(
        (field) => field !== "exclude_zeroes"
      );
    }

    this._data$.next([]);

    this.reportsService
      .getFilteredReport({
        ...this.reportReq,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          this.pageCount = data.num_pages;
          this.page = data.page;
          const updatedData = this.updateTransactionAmount(data);
          this._data$.next(updatedData);
          if (isEmpty(data.results)) this.noTableData = true;
          const firstRow = data.results && data.results[0];
          this.hasPctData = !!(firstRow && Object.keys(firstRow).some((key) => key.endsWith('_pct')));
          this.hasTotalsRow = data.results && data.has_total_row;
          this.summaryData = data.summary_row || null;

          this.totalResultCount = data.count;
          this.isTableLoading = false;
          this.isReportGenerated.emit(true);
        },
        (error) => {
          this.isReportGenerated.emit(true);
          this.noTableData = true;
          this.handleError(error);
          this._data$.error(error);
          this.isTableLoading = false;
        }
      );
  }

  getFieldLabel(field: string): string {
    if (field.startsWith("exclude_") || this.fieldData[field] == undefined) {
      return "";
    }
    return this.fieldData[field].label;
  }

  toggleExpandQuestion(rowIndex: number) {
    this.isQuestionExpanded.forEach((_, index) => {
      if (index !== rowIndex) {
        this.isQuestionExpanded[index] = false;
      }
    });

    this.isQuestionExpanded[rowIndex] = !this.isQuestionExpanded[rowIndex];
  }

  isQuestionRowExpanded(rowIndex: number): boolean {
    return !!this.isQuestionExpanded[rowIndex];
  }

  isAnswerRowExpanded(rowIndex: number): boolean {
    return !!this.isAnswerExpanded[rowIndex];
  }

  toggleExpandAnswer(rowIndex: number) {
    this.isAnswerExpanded.forEach((_, index) => {
      if (index !== rowIndex) {
        this.isAnswerExpanded[index] = false;
      }
    });

    this.isAnswerExpanded[rowIndex] = !this.isAnswerExpanded[rowIndex];
  }

  truncateText(text: string, charLimit: number = 22): string {
    if (text.length > charLimit) {
      return text.slice(0, charLimit) + '...';
    }
    return text;
  }

  shouldShowMore(text: string, charLimit: number = 22): boolean {
    return text.length > charLimit;
  }

  openSessionDetails(event, sessionId) {
    event.preventDefault();
    this.selectedSessionId = sessionId;
    this.modalService.getModal('sessionDetailModal').open();
  }

  revertToISOFormat (dateTimeValue) {
    let result = '';
    
    if (dateTimeValue) {
      let dateTimeMoment;
      
      // Check if the input is in the formatted string format "MMM DD, YYYY - hh:mm:ss A"
      if (typeof dateTimeValue === 'string' && dateTimeValue.includes(' - ')) {
        const [datePart, timePart] = dateTimeValue.split(' - ');
        const combinedDateTime = `${datePart} ${timePart}`;
        dateTimeMoment = moment.tz(combinedDateTime, 'MMM DD, YYYY hh:mm:ss A', 'EST');
      } else {
        // For original date format
        dateTimeMoment = moment(dateTimeValue);
      }
      
      result = dateTimeMoment.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]');
    }
    
    return result;
  };

  openTransactionDetailReport(event, txnId: string, txnDate: string) {
    event.preventDefault();
    if (txnId && txnDate) {
      const date = this.revertToISOFormat(txnDate);
      const url = this.router.serializeUrl(
        this.router.createUrlTree(['/checkouts/transaction-details', txnId, date])
      );
      window.open(url, '_blank');
    }
  }

  isJourneyLink(field: string, row: ReportSessionInput): boolean {
    return field === ReportDimensions.JourneyID && !!this.journeyType && row[field] !== null && row[field] !== undefined;
  }

  openCustomerJourney(event: Event, journeyId: number) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/customer-journey-report', this.journeyType, journeyId])
    );
    window.open(url, '_blank');
  }

  openReportSessionDetails(event, sessionId) {
    event.preventDefault();
    this.selectedSessionId = sessionId;
    this.modalService.getModal('spSessionDetailModal').open();
  }

  updateTransactionAmount(data: Pager) {
    const updatedData = data.results.map((obj) => {
      const updatedObj = { ...obj };

      if ("amount" in updatedObj) {
        updatedObj["amount"] = "$" + updatedObj.amount;
      }
      return updatedObj;
    });
    return updatedData;
  }

  protected orderBy(ordering: string[]) {
    this.onOrder.emit(ordering)
  }

  public resetPager() {
    super.resetPager();
  }

  getSummaryValue(column): string {
    let fieldName = column.prop;
    let result = '';
    if (fieldName === this.selectedFields[0]) {
      result = 'Summary';
    } else if (this.summaryData) {
      let value = this.summaryData[fieldName];
      if (value || value === 0) {
        result = isRateOrPctField(fieldName) ? String(value) + '%' : String(value);
      }
    }
    return result;
  }

  formatHeader(header: string): string {
    let result = '';
    if (this.fieldLabels[header]) {
      result = this.fieldLabels[header];
    } else if (this.fieldData[header]) {
      const entry = this.fieldData[header];
      if (this.hasPctData && entry['pct_label']) {
        result = entry['pct_label'];
      } else {
        result = entry.custom_label || entry.label;
      }
    }
    return result;
  }

  getHelpText(field: string): string {
    let helpText = '';
    if (this.fieldData[field]) {
      helpText = this.fieldData[field].detailHelpText || '';
    }
    return helpText;
  }
}
